import { type ApolloClient, type NormalizedCacheObject, gql } from '@apollo/client/core';
import type { DeleteWebhookSubscriptionMutationInput, Mutation, Query } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as WebhookSubscription from './WebhookSubscription';
import * as WebhookSubscriptionInput from './WebhookSubscriptionInput';
import { WEBHOOK_SUBSCRIPTION_FIELDS } from './fragments';

export type CreatedWebhookSubscription = { id: number; signingSecret: string };

export type RotatedWebhookSubscriptionSecret = { id: number; signingSecret: string; previousSecretExpiresAt: string | null };

export const WEBHOOK_SUBSCRIPTIONS_QUERY = gql`
  query WebhookSubscriptions {
    webhookSubscriptions {
      ...WebhookSubscriptionFields
    }
  }
  ${WEBHOOK_SUBSCRIPTION_FIELDS}
`;

export const fetchWebhookSubscriptions = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<WebhookSubscription.WebhookSubscription[]> => {
  const { data } = await client.query<Pick<Query, 'webhookSubscriptions'>>({
    query: WEBHOOK_SUBSCRIPTIONS_QUERY,
    fetchPolicy: 'network-only',
  });

  return data.webhookSubscriptions.map(WebhookSubscription.fromGql);
};

export const CREATE_WEBHOOK_SUBSCRIPTION_MUTATION = gql`
  mutation createWebhookSubscription($input: CreateWebhookSubscriptionMutationInput!) {
    createWebhookSubscription(input: $input) {
      webhookSubscription {
        id
        signingSecret
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const createWebhookSubscription = async (
  client: ApolloClient<NormalizedCacheObject>,
  subscription: WebhookSubscriptionInput.WebhookSubscriptionInput,
): Promise<VPResult<CreatedWebhookSubscription, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: CREATE_WEBHOOK_SUBSCRIPTION_MUTATION,
    variables: { input: WebhookSubscriptionInput.toGqlInput(subscription) },
    // The signing secret is readable exactly once; it must not linger in the normalized cache.
    fetchPolicy: 'no-cache',
  });

  const payload = data?.createWebhookSubscription;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.webhookSubscription) {
    return { success: false, errors: [{ path: '', message: 'Failed to create the webhook subscription.' }] };
  }

  const { id, signingSecret } = payload.webhookSubscription;

  return { success: true, data: { id, signingSecret } };
};

export const UPDATE_WEBHOOK_SUBSCRIPTION_MUTATION = gql`
  mutation updateWebhookSubscription($input: UpdateWebhookSubscriptionMutationInput!) {
    updateWebhookSubscription(input: $input) {
      webhookSubscription {
        ...WebhookSubscriptionFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${WEBHOOK_SUBSCRIPTION_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const updateWebhookSubscription = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  subscription: WebhookSubscriptionInput.WebhookSubscriptionInput,
): Promise<VPResult<WebhookSubscription.WebhookSubscription, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPDATE_WEBHOOK_SUBSCRIPTION_MUTATION,
    variables: { input: WebhookSubscriptionInput.toGqlUpdateInput(id, subscription) },
  });

  const payload = data?.updateWebhookSubscription;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.webhookSubscription) {
    return { success: false, errors: [{ path: '', message: 'Failed to update the webhook subscription.' }] };
  }

  return { success: true, data: WebhookSubscription.fromGql(payload.webhookSubscription) };
};

export const DELETE_WEBHOOK_SUBSCRIPTION_MUTATION = gql`
  mutation deleteWebhookSubscription($input: DeleteWebhookSubscriptionMutationInput!) {
    deleteWebhookSubscription(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const deleteWebhookSubscription = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const input: DeleteWebhookSubscriptionMutationInput = { id: String(id) };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_WEBHOOK_SUBSCRIPTION_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteWebhookSubscription;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  return { success: false, errors: [{ path: '', message: 'Failed to delete the webhook subscription.' }] };
};

export const ROTATE_WEBHOOK_SUBSCRIPTION_SECRET_MUTATION = gql`
  mutation rotateWebhookSubscriptionSecret($input: RotateWebhookSubscriptionSecretMutationInput!) {
    rotateWebhookSubscriptionSecret(input: $input) {
      webhookSubscription {
        id
        signingSecret
        previousSecretExpiresAt
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const rotateWebhookSubscriptionSecret = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<RotatedWebhookSubscriptionSecret, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: ROTATE_WEBHOOK_SUBSCRIPTION_SECRET_MUTATION,
    variables: { input: { id: String(id) } },
    fetchPolicy: 'no-cache',
  });

  const payload = data?.rotateWebhookSubscriptionSecret;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.webhookSubscription) {
    return { success: false, errors: [{ path: '', message: 'Failed to rotate the webhook signing secret.' }] };
  }

  const { id: rotatedId, signingSecret, previousSecretExpiresAt } = payload.webhookSubscription;

  return { success: true, data: { id: rotatedId, signingSecret, previousSecretExpiresAt: previousSecretExpiresAt ?? null } };
};

export type SwitchedWebhookSubscription = {
  subscription: WebhookSubscription.WebhookSubscription;
  notices: VPErrors.VPError[];
};

export const SET_WEBHOOK_SUBSCRIPTION_DELIVERY_MUTATION = gql`
  mutation setWebhookSubscriptionDelivery($input: SetWebhookSubscriptionDeliveryMutationInput!) {
    setWebhookSubscriptionDelivery(input: $input) {
      webhookSubscription {
        ...WebhookSubscriptionFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${WEBHOOK_SUBSCRIPTION_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const setWebhookSubscriptionDelivery = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
  enabled: boolean,
): Promise<VPResult<SwitchedWebhookSubscription, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: SET_WEBHOOK_SUBSCRIPTION_DELIVERY_MUTATION,
    variables: { input: { id, enabled } },
  });

  const payload = data?.setWebhookSubscriptionDelivery;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A returned subscription means it switched; errors then only explain why it still is not delivering.
  if (payload?.webhookSubscription) {
    return { success: true, data: { subscription: WebhookSubscription.fromGql(payload.webhookSubscription), notices: errors } };
  }

  if (errors.length) {
    return { success: false, errors };
  }

  return { success: false, errors: [{ path: '', message: 'The webhook subscription could not be switched.' }] };
};

export const TAKE_WEBHOOK_SUBSCRIPTION_OWNERSHIP_MUTATION = gql`
  mutation takeWebhookSubscriptionOwnership($input: TakeWebhookSubscriptionOwnershipMutationInput!) {
    takeWebhookSubscriptionOwnership(input: $input) {
      webhookSubscription {
        ...WebhookSubscriptionFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${WEBHOOK_SUBSCRIPTION_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const takeWebhookSubscriptionOwnership = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<WebhookSubscription.WebhookSubscription, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: TAKE_WEBHOOK_SUBSCRIPTION_OWNERSHIP_MUTATION,
    variables: { input: { id: String(id) } },
  });

  const payload = data?.takeWebhookSubscriptionOwnership;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.webhookSubscription) {
    return { success: false, errors: [{ path: '', message: 'Failed to take ownership of the webhook subscription.' }] };
  }

  return { success: true, data: WebhookSubscription.fromGql(payload.webhookSubscription) };
};
